import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Snackbar } from '@mui/material';
import { Close } from '@mui/icons-material/';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';

import { DICE_TYPES } from './../../common/constants';
import { getAlienResultPresentation, isAlienDiceResult } from './../../common/gameSystems/alien/formatAlienResult';
import { getDicesResult } from './../../common/services/l5rDiceTranslator';
import { diceResultsState, diceRollState } from './../../common/recoil/UserInfo/atoms';
import { isNextDiceExplosiveState, diceRollMetaState } from './../../common/recoil/GameSystem/gameSystemAtom';

const DiceResultAlert = ({ isLocalUser, results, userName }) => {
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const { t, i18n } = useTranslation();
	const [shownResults, setShownResults] = useState([]);
	const [roll, setRoll] = useRecoilState(diceRollState);
	const [isNextDiceExplosive, setIsNextDiceExplosive] = useRecoilState(isNextDiceExplosiveState);
	// const diceMeta = useRecoilValue(diceRollMetaState);

	const onClickDismiss = (key) => () => {
		closeSnackbar(key);
	};

	const onExplodeDices = (key, explosiveNotations) => {
		closeSnackbar(key);
		setIsNextDiceExplosive(true);
		setRoll(explosiveNotations);
	};

	const showCustomDiceAlert = (diceResult) => {
		const opts = {
			autoHideDuration: 6000,
			anchorOrigin: { vertical: 'top', horizontal: 'center' },
			action: (key) => <Button onClick={() => closeSnackbar(key)}>{t('diceResults.alien.close')}</Button>,
		};
		if (isAlienDiceResult(diceResult)) {
			const presentation = getAlienResultPresentation(diceResult, {
				t,
				locale: i18n.resolvedLanguage || i18n.language,
				userName,
			});
			if (presentation) {
				const prefix =
					presentation.severity === 'success'
						? '✅'
						: presentation.severity === 'warning'
							? '⚠️'
							: '❌';
				enqueueSnackbar(`${prefix} ${presentation.message}`, {
					variant: presentation.severity,
					...opts,
				});
				return;
			}
		}
		if (diceResult.summary) {
			if (diceResult.panicRoll) {
				const severe = Number(diceResult.value) >= 10;
				enqueueSnackbar(
					`${severe ? '❌' : '⚠️'} ${diceResult.notationMsg || `${userName} resuelve pánico: ${diceResult.summary}`}`,
					{ variant: severe ? 'error' : 'warning', ...opts }
				);
				return;
			}
			const hasSuccess = Number(diceResult.value) > 0;
			const variant = hasSuccess
				? diceResult.panic
					? 'warning'
					: 'success'
				: 'error';
			const prefix = hasSuccess
				? diceResult.panic
					? '⚠️'
					: '✅'
				: '❌';
			enqueueSnackbar(
				`${prefix} ${diceResult.notationMsg || `${userName} ha hecho: ${diceResult.summary}`}`,
				{ variant, ...opts }
			);
			return;
		}
		if (diceResult.success) {
			enqueueSnackbar(`✅ ${diceResult.notationMsg}`, { variant: 'success', ...opts });
		} else if (diceResult.partialSuccess) {
			enqueueSnackbar(`⚠️ ${diceResult.notationMsg}`, { variant: 'warning', ...opts });
		} else {
			enqueueSnackbar(`❌ ${diceResult.notationMsg}`, { variant: 'error', ...opts });
		}
	};

	useEffect(() => {
		if (results?.length > 0 && Array.isArray(results)) {
			let dicesResult = getDicesResult(results, shownResults);
			setShownResults(results);
			if (dicesResult.someResult) {
				enqueueSnackbar(`${userName} ha sacado ${dicesResult.text}`, {
					variant: 'info',
					anchorOrigin: {
						vertical: 'bottom',
						horizontal: 'right',
					},
					action: (key) => (
						<Button onClick={onClickDismiss(key)}>
							<Close color={'white'}></Close>
						</Button>
					),
					preventDuplicate: true,
				});
				if (isLocalUser && dicesResult.explosiveNotations.length > 0) {
					setTimeout(() => {
						enqueueSnackbar(
							`Hay ${dicesResult.explosiveNotations.length} dados relanzables. ¿Quieres lanzarlos?`,
							{
								variant: 'success',
								anchorOrigin: {
									vertical: 'top',
									horizontal: 'center',
								},
								action: (key) => (
									<Button
										onClick={() => {
											onExplodeDices(key, dicesResult.explosiveNotations);
										}}>
										Lanzar
									</Button>
								),
							}
						);
					}, 1500);
				}
			} else {
				const customdices = results.filter((x) => x.type === DICE_TYPES.CUSTOM);
				if (customdices.length > 0) {
					customdices.forEach((diceResult) => {
						showCustomDiceAlert(diceResult);
					});
				}				
			}
		}
	}, [results]);

	return <Box></Box>;
};

export default DiceResultAlert;
